import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import * as api from '../services/api';
import { AppNotification, UserRole } from '../types';
import { supabase } from '../services/supabase';

interface NotificationsContextType {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  deleteReadNotifications: () => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  sendTestPush: () => Promise<void>;
  checkDeadlines: () => Promise<void>;
  telegramChatId: string | null;
  updateTelegramChatId: (chatId: string) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

const DELETED_NOTIFICATIONS_KEY = 'fairyrealm_deleted_notification_ids';

const getDeletedNotificationIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DELETED_NOTIFICATIONS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const addDeletedNotificationIds = (ids: string | string[]): void => {
  try {
    const current = getDeletedNotificationIds();
    const toAdd = Array.isArray(ids) ? ids : [ids];
    toAdd.forEach(id => current.add(id));
    const trimmed = Array.from(current).slice(-1000);
    localStorage.setItem(DELETED_NOTIFICATIONS_KEY, JSON.stringify(trimmed));
  } catch {
    // Ignore storage error
  }
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isNotificationUuid = (val: unknown): val is string => typeof val === 'string' && UUID_REGEX.test(val.trim());

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [telegramChatId, setTelegramChatId] = useState<string | null>(null);

  useEffect(() => {
    if (user?.telegramChatId) {
      setTelegramChatId(user.telegramChatId);
    } else {
      setTelegramChatId(null);
    }
  }, [user]);

  const getTargetIds = useCallback(async (): Promise<string[]> => {
    if (!user) return [];
    const validIds: string[] = [];
    if (isNotificationUuid(user.clientId)) validIds.push(user.clientId);
    if (isNotificationUuid(user.id)) validIds.push(user.id);

    if (user.role === UserRole.DOMINA) {
      try {
        const dominaId = await api.getDominaId();
        if (dominaId && isNotificationUuid(dominaId) && !validIds.includes(dominaId)) {
          validIds.push(dominaId);
        }
      } catch {
        // ignore
      }
    }
    return Array.from(new Set(validIds));
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const targetIds = await getTargetIds();
      const results = await Promise.all(
        targetIds.map(tid => api.getAppNotifications(tid).catch(() => []))
      );
      
      const allFetched = results.flat();
      const uniqueMap = new Map<string, AppNotification>();
      allFetched.forEach(n => {
        if (!uniqueMap.has(n.id)) uniqueMap.set(n.id, n);
      });

      const deletedIds = getDeletedNotificationIds();
      const validNotifications = Array.from(uniqueMap.values())
        .filter(n => !deletedIds.has(n.id))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setNotifications(validNotifications);
      setUnreadCount(validNotifications.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user, getTargetIds]);

  const checkDeadlines = useCallback(async () => {
    if (!user) return;
    try {
      const targetId = user.clientId || user.id;
      await api.checkAndNotifyTaskDeadlines(user.role, targetId);
      await fetchNotifications();
    } catch (error) {
      console.warn('Failed to check task deadlines:', error);
    }
  }, [user, fetchNotifications]);

  // App-interne Benachrichtigungen (Supabase Realtime) & Deadline Check
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const targetId = user.clientId || user.id;

    // Register Domina ID if current user is Domina
    if (user.role === UserRole.DOMINA) {
      api.registerDomina(user.id).catch(e => console.warn("Failed to register Domina ID", e));
    }

    fetchNotifications();
    checkDeadlines();

    // Check deadlines every 2 minutes
    const deadlineInterval = setInterval(() => {
      checkDeadlines();
    }, 2 * 60 * 1000);

    const channel = supabase
      .channel(`notifications-${targetId}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'app_notifications',
          filter: `user_id=eq.${targetId}`
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      clearInterval(deadlineInterval);
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications, checkDeadlines]);

  const markAsRead = async (id: string) => {
    try {
      await api.markNotificationAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;
    try {
      const targetIds = await getTargetIds();
      await Promise.all(targetIds.map(tid => api.markAllNotificationsAsRead(tid).catch(() => {})));
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    // 1. Instantly record deletion locally so it never reappears on reload
    addDeletedNotificationIds(id);

    // 2. Instantly update React state
    const wasUnread = notifications.find(n => n.id === id && !n.is_read);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (wasUnread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    // 3. Persist deletion in Supabase & Server
    try {
      await api.deleteAppNotification(id);
    } catch (error) {
      console.warn('Failed to delete notification on backend:', error);
    }
  };

  const deleteReadNotifications = async () => {
    const readItems = notifications.filter(n => n.is_read);
    if (readItems.length === 0) return;
    const readIds = readItems.map(n => n.id);

    // 1. Record deletion locally
    addDeletedNotificationIds(readIds);

    // 2. Update state
    setNotifications(prev => prev.filter(n => !n.is_read));

    // 3. Persist deletion in backend
    try {
      const targetIds = await getTargetIds();
      await api.deleteAllAppNotifications(targetIds, true);
      await fetch('/api/notifications/delete-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: readIds, userIds: targetIds, onlyRead: true })
      }).catch(() => {});
    } catch (error) {
      console.warn('Failed to delete read notifications on backend:', error);
    }
  };

  const deleteAllNotifications = async () => {
    if (!user || notifications.length === 0) return;
    const allIds = notifications.map(n => n.id);

    // 1. Record deletion locally
    addDeletedNotificationIds(allIds);

    // 2. Update state
    setNotifications([]);
    setUnreadCount(0);

    // 3. Persist deletion in backend
    try {
      const targetIds = await getTargetIds();
      await api.deleteAllAppNotifications(targetIds, false);
      await fetch('/api/notifications/delete-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: allIds, userIds: targetIds, onlyRead: false })
      }).catch(() => {});
    } catch (error) {
      console.error('Failed to delete all notifications on backend:', error);
    }
  };

  const sendTestPush = async () => {
      if (!user) return;
      try {
          const targetId = user.clientId || user.id;
          await api.triggerPushNotification(targetId, "Dies ist ein Test-Signal aus dem Fairyrealm. Erreicht es dich?");
      } catch (e) {
          console.error("Test push failed", e);
          throw e;
      }
  };

  const updateTelegramChatId = async (chatId: string) => {
    if (!user) return;
    try {
      const id = user.clientId || user.id;
      console.log(`Updating Telegram Chat ID for ${user.role} (ID: ${id}) to: ${chatId}`);
      await api.updateTelegramChatId(id, user.role, chatId);
      setTelegramChatId(chatId);
    } catch (e) {
      console.error("Failed to update Telegram Chat ID", e);
      throw e;
    }
  };

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteReadNotifications,
    deleteAllNotifications,
    sendTestPush,
    checkDeadlines,
    telegramChatId,
    updateTelegramChatId
  }), [
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteReadNotifications,
    deleteAllNotifications,
    sendTestPush,
    checkDeadlines,
    telegramChatId
  ]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
